import { deleteTrackBookmark, postTrackBookmark } from "@api/bookmarks";
import DropDown from "@components/Dropdown";
import ErrorFallback from "@components/ErrorFallback";
import FadedText from "@components/FadedText";
import { Column, LineClamp, Row } from "@components/Layout";
import PlayProgress from "@components/PlayProgress";
import ShareDialog from "@components/ShareDialog";
import { Template } from "@components/Template";
import { toast } from "@components/Toaster";
import useI18n from "@contexts/I18nProvider/hooks/useI18n";
import { Button, Image } from "@kobalte/core";
import { playerContext } from "@layouts/App/context/Player";
import PlayButton from "@layouts/App/context/Player/components/PlayButton";
import { A } from "@solidjs/router";
import { formatPlayTime } from "@src/app/utils";
import { FaRegularBookmark, FaRegularFlag, FaSolidBookmark, FaSolidEllipsis } from "solid-icons/fa";
import { FiShare2 } from "solid-icons/fi";
import { ErrorBoundary, Show, type VoidComponent, createMemo, createSignal, useContext } from "solid-js";
import { AuthorName, PlayableTrackCard, TrackImage, TrackName } from "./styled";
import type { PlayableTrackProps } from "./types";

const Inner: VoidComponent<Omit<PlayableTrackProps, "even">> = (props) => {
	const { t } = useI18n();
	const [share, setShare] = createSignal(false);
	const { player } = useContext(playerContext);

	const handleBookmark = () => {
		const p = props.track.bookmarked
			? deleteTrackBookmark(props.track.id, props.track.platform)
			: postTrackBookmark(props.track.id, props.track.platform);
		p.then(props.onBookmark);
	};

	const handleReport = () => {
		toast.success(t("app.programme.reporting.success"));
	};

	const progress = createMemo(() => {
		const track = player.queue[player.track];
		if (!track || track.id !== props.track.id || track.tracklistUuid !== props.tracklist?.uuid) return 0;
		return player.time / props.track.length;
	});

	return (
		<>
			<Row gap="20px">
				<TrackImage clickable={!!props.onImageClick}>
					<Image.Img src={props.track.photo} onclick={props.onImageClick} />
					<Image.Fallback>
						<Template w="100%" h="100%" />
					</Image.Fallback>
				</TrackImage>

				<Column>
					<Row gap="5px">
						<Row>
							<Column gap="10px">
								<TrackName fontSize="15px" fontWeight="700" class={LineClamp}>
									{props.track.name}
								</TrackName>
								<Show
									when={props.track.author.uid}
									fallback={
										<AuthorName fontWeight="700" fontSize="13px">
											{props.track.author.nickname}
										</AuthorName>
									}
								>
									<A href={`/profile/${props.track.author.uid}`}>
										<AuthorName fontWeight="700" fontSize="13px">
											{props.track.author.nickname}
										</AuthorName>
									</A>
								</Show>
							</Column>
						</Row>

						<Show when={props.track.platform === "voxtox" && !props.blur}>
							<Column flexShrink={0} w="max-content">
								<DropDown
									items={[
										{
											label: t("app.programme.report"),
											icon: <FaRegularFlag />,
											onClick: handleReport,
										},
									]}
								>
									<FaSolidEllipsis style={{ "user-select": "none", "pointer-events": "none" }} />
								</DropDown>
							</Column>
						</Show>
					</Row>
					<Row padding="10px 0 0" alignItems="center" gap="10px">
						<PlayButton
							diameter="45px"
							onClick={props.onPlay}
							resume
							track={{ id: props.track.id, index: props.index === undefined ? -1 : props.index }}
							tracklistUuid={props.tracklist?.uuid}
						/>
						<PlayProgress progress={progress()} />
						<FadedText>
							{formatPlayTime(props.track.length, {
								s: true,
								h: props.track.length >= 3600,
								m: props.hidePlaying || props.track.length >= 3600,
							})}
						</FadedText>
					</Row>
					<Show when={!props.public}>
						<Row justifyContent="flex-end" gap="15px">
							<Show when={props.tracklist} keyed>
								{(tr) => (
									<>
										<ShareDialog
											tracklist={share() ? tr : undefined}
											trackIndex={props.index}
											onOpenChange={setShare}
										/>
										<Button.Root onclick={() => setShare(true)}>
											<FadedText>
												<Row alignItems="center" gap="5px">
													<FiShare2 />
													{t("app.programme.share")}
												</Row>
											</FadedText>
										</Button.Root>
									</>
								)}
							</Show>
							<Button.Root onclick={handleBookmark}>
								<FadedText>
									<Row alignItems="center">
										<Show when={props.track.bookmarked} fallback={<FaRegularBookmark />}>
											<FaSolidBookmark />
										</Show>
									</Row>
								</FadedText>
							</Button.Root>
						</Row>
					</Show>
				</Column>
			</Row>
		</>
	);
};

const PlayableTrack: VoidComponent<PlayableTrackProps> = (props) => {
	const { player } = useContext(playerContext);

	const playing = createMemo(() => {
		const track = player.queue[player.track];
		return (
			track &&
			track.id === props.track.id &&
			player.playing &&
			props.tracklist?.uuid === track.tracklistUuid &&
			!props.hidePlaying
		);
	});

	return (
		<Row justifyContent="center" padding="10px 0 0" h="150px">
			<PlayableTrackCard maxWidth="756px" padding="15px" even={props.even} blur={props.blur} playing={playing()}>
				<ErrorBoundary fallback={<ErrorFallback borderRadius="16px" />}>
					<Inner {...props} />
				</ErrorBoundary>
			</PlayableTrackCard>
		</Row>
	);
};

export default PlayableTrack;
